import { useEffect, useState } from 'react';
import { resolvePersonByName } from '../services/tmdbService';

/**
 * Résout des noms d'acteurs en fiches TMDB (photo + identifiant).
 *
 * Le catalogue local ne stocke que des chaînes de caractères : ce pont est ce
 * qui permet d'afficher un visage et d'ouvrir une fiche depuis un simple nom.
 *
 * Les appels sont bridés à quelques requêtes simultanées pour ne pas saturer
 * le réseau mobile, et `tmdbService` mémorise déjà les réponses pour la session.
 *
 * @param {string[]} names
 * @param {number}   concurrency
 * @returns {{people: Record<string, object|null>, loading: boolean}}
 */
export default function usePersonPhotos(names, concurrency = 4) {
  const [people, setPeople] = useState({});
  const [loading, setLoading] = useState(false);

  // Clé stable : évite de relancer la résolution à chaque rendu du parent.
  const signature = (names || []).join('|');

  useEffect(() => {
    const list = (names || []).filter(Boolean);
    if (!list.length) {
      setPeople({});
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    const queue = [...list];
    const found = {};

    const worker = async () => {
      while (queue.length && !cancelled) {
        const name = queue.shift();
        try {
          found[name] = await resolvePersonByName(name);
        } catch {
          found[name] = null;
        }
        // Rendu progressif : les visages apparaissent au fil de l'eau
        // plutôt qu'en un bloc après la dernière requête.
        if (!cancelled) setPeople({ ...found });
      }
    };

    Promise.all(Array.from({ length: Math.min(concurrency, list.length) }, worker)).finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, concurrency]);

  return { people, loading };
}
